import type { Iti } from 'intl-tel-input';
import type { CountryCode } from 'libphonenumber-js';
import { getExampleNumber } from 'libphonenumber-js/mobile';
import examples from 'libphonenumber-js/mobile/examples';

export type PhoneInputOptions = {
    initialCountry?: string;
    /** Avoid clipping inside overflow:auto parents (simulator form). */
    dropdownContainer?: HTMLElement;
};

let intlTelInputModule: typeof import('intl-tel-input') | null = null;

async function loadIntlTelInputModule(): Promise<typeof import('intl-tel-input')> {
    if (!intlTelInputModule) {
        const [mod] = await Promise.all([
            import('intl-tel-input'),
            import('intl-tel-input/build/css/intlTelInput.css'),
        ]);
        intlTelInputModule = mod;
    }

    return intlTelInputModule;
}

const PLACEHOLDER_COUNTRIES = new Set(['FR', 'BE', 'CH']);

/** National-format example number for the given ISO2 country (FR/BE/CH). */
export function getPhonePlaceholder(countryIso2: string): string {
    const iso2 = countryIso2.toUpperCase();
    if (!PLACEHOLDER_COUNTRIES.has(iso2)) {
        return '';
    }

    const example = getExampleNumber(iso2 as CountryCode, examples);

    return example?.formatNational() ?? '';
}

/**
 * Attach intl-tel-input to a tel field (FR default).
 * Matches PhoneInput.vue: mode="international" (dial code in the field, not separate).
 */
export async function initPhoneInput(
    input: HTMLInputElement,
    options: PhoneInputOptions = {},
): Promise<Iti> {
    const { default: intlTelInput } = await loadIntlTelInputModule();

    return intlTelInput(input, {
        initialCountry: (options.initialCountry ?? 'fr') as 'fr',
        onlyCountries: ['fr', 'be', 'ch'],
        separateDialCode: false,
        // Override any SSR placeholder and refresh on countrychange.
        autoPlaceholder: 'aggressive',
        // Mirror vue-tel-input / PhoneInput.vue: a plain scrollable country
        // list with no search box at the top.
        countrySearch: false,
        dropdownContainer: options.dropdownContainer ?? document.body,
        loadUtils: () => import('intl-tel-input/utils'),
    });
}

/**
 * Recompute input padding after the field becomes visible (intl-tel-input
 * measures .iti__selected-country width at init — wrong when a parent is [hidden]).
 */
export function refreshPhoneInputLayout(input: HTMLInputElement): void {
    const selectedCountry = input
        .closest('.iti')
        ?.querySelector<HTMLElement>('.iti__selected-country');
    if (!selectedCountry) {
        return;
    }

    const selectedCountryWidth =
        selectedCountry.offsetWidth > 0 ? selectedCountry.offsetWidth : 42;
    input.style.paddingLeft = `${selectedCountryWidth + 6}px`;
}

/**
 * E.164 number when valid, otherwise trimmed national input value.
 */
export function getPhoneNumber(iti: Iti, input: HTMLInputElement): string {
    const full = iti.getNumber();

    return full !== '' ? full : input.value.trim();
}

/**
 * True when the field has enough digits to count as filled (login modal uses 6).
 */
export function isPhoneInputFilled(input: HTMLInputElement): boolean {
    const digits = input.value.replace(/\D/g, '');

    return digits.length >= 6;
}
